// Reusable Schema.org JSON-LD Generators for Cameroon Land Market
// Compliant with Google Rich Results Specifications & Schema.org Standards
// Rule: Never invent data; omit any property whose data is missing.

export const BASE_URL = 'https://cameroonlandmarket.com';

export interface BreadcrumbItem {
  name: string;
  url?: string;
}

export interface FaqItem {
  q?: string;
  question?: string;
  a?: string;
  answer?: string;
}

export interface RealEstateListingData {
  name?: string;
  title?: string;
  url?: string;
  canonicalUrl?: string;
  slug?: string;
  id?: string;
  datePosted?: string | Date;
  created_at?: string | Date | { toDate?: () => Date };
  createdAt?: string | Date | { toDate?: () => Date };
  image?: string | string[];
  images?: string[];
  primary_image?: string;
  imageUrl?: string;
  description?: string;
  price?: number | string;
  city?: string;
  locality?: string;
  location?: string;
  region?: string;
  addressRegion?: string;
  localized_title?: { en?: string; fr?: string };
  localized_description?: { en?: string; fr?: string };
}

export interface ArticleGuideData {
  headline?: string;
  title?: string;
  description?: string;
  author?: string | { '@type': string; name: string; url?: string };
  datePublished?: string | Date | { toDate?: () => Date };
  publishedAt?: string | Date | { toDate?: () => Date };
  dateModified?: string | Date | { toDate?: () => Date };
  updatedAt?: string | Date | { toDate?: () => Date };
  url?: string;
  canonicalUrl?: string;
  path?: string;
  inLanguage?: string;
}

/**
 * 1. Home Page: WebSite and Organization Structured Data
 * Rule: SearchAction is strictly omitted because site search does not work with a query parameter.
 */
export function buildHomeJsonLd(options: { lang?: 'en' | 'fr'; baseUrl?: string; sameAs?: string[] } = {}) {
  const lang = options.lang || 'en';
  const isFr = lang === 'fr';
  const baseUrl = (options.baseUrl || BASE_URL).replace(/\/+$/, '');
  const homeUrl = `${baseUrl}/${isFr ? 'fr' : 'en'}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        'name': 'Cameroon Land Market',
        'url': homeUrl,
        'inLanguage': isFr ? 'fr-CM' : 'en-CM',
        'description': isFr
          ? 'Plateforme indépendante de petites annonces immobilières gratuites : terrains titrés, maisons et appartements dans les 10 régions du Cameroun.'
          : 'Find titled land, houses, rental apartments, commercial property, and short stays across all 10 regions of Cameroon.',
        'publisher': {
          '@id': `${baseUrl}/#organization`,
        },
        // SearchAction omitted: Cameroon Land Market does not have a working site-wide ?q= search parameter.
      },
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        'name': 'Cameroon Land Market',
        'url': homeUrl,
        'logo': `${baseUrl}/cameroon-land-market-logo.png`,
        ...(Array.isArray(options.sameAs) && options.sameAs.length > 0 ? { 'sameAs': options.sameAs } : {}),
      },
    ],
  };
}

/**
 * 2. Inner Pages: BreadcrumbList matching visible breadcrumbs exactly.
 * Rule: Only items with a real URL get an 'item' property. Terminal/active items omit 'item'.
 */
export function buildBreadcrumbListJsonLd(
  items: BreadcrumbItem[],
  options: { lang?: 'en' | 'fr'; baseUrl?: string } = {}
) {
  const lang = options.lang || 'en';
  const baseUrl = (options.baseUrl || BASE_URL).replace(/\/+$/, '');

  return {
    '@type': 'BreadcrumbList',
    'inLanguage': lang === 'fr' ? 'fr-CM' : 'en-CM',
    'itemListElement': items.map((item, index) => {
      const listItem: {
        '@type': string;
        position: number;
        name: string;
        item?: string;
      } = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
      };

      if (item.url && item.url.trim()) {
        const fullUrl = item.url.startsWith('http')
          ? item.url
          : `${baseUrl}${item.url.startsWith('/') ? item.url : '/' + item.url}`;
        listItem.item = fullUrl;
      }

      return listItem;
    }),
  };
}

/**
 * 3. FAQ Section: FAQPage generated from identical visible FAQ data.
 */
export function buildFaqPageJsonLd(
  faqItems: FaqItem[],
  options: { lang?: 'en' | 'fr' } = {}
) {
  const lang = options.lang || 'en';

  const validFaqs = (faqItems || []).filter((item) => {
    const q = item.q || item.question;
    const a = item.a || item.answer;
    return Boolean(q && q.trim() && a && a.trim());
  });

  return {
    '@type': 'FAQPage',
    'inLanguage': lang === 'fr' ? 'fr-CM' : 'en-CM',
    'mainEntity': validFaqs.map((item) => ({
      '@type': 'Question',
      'name': (item.q || item.question)!.trim(),
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': (item.a || item.answer)!.trim(),
      },
    })),
  };
}

/**
 * 4. Listing Pages: RealEstateListing
 * Rule: Never invent data; omit any property whose data is missing.
 */
export function buildRealEstateListingJsonLd(
  data: RealEstateListingData,
  options: { lang?: 'en' | 'fr'; baseUrl?: string } = {}
) {
  if (!data) return null;
  const lang = options.lang || 'en';
  const baseUrl = (options.baseUrl || BASE_URL).replace(/\/+$/, '');

  const listing: Record<string, any> = {
    '@type': 'RealEstateListing',
  };

  // Name (omit if missing)
  const name =
    (data.localized_title && data.localized_title[lang]) ||
    data.name ||
    data.title;
  if (name && typeof name === 'string' && name.trim()) {
    listing.name = name.trim();
  }

  // URL (omit if missing)
  let url = data.url || data.canonicalUrl;
  if (!url && (data.slug || data.id)) {
    const slug = data.slug || data.id;
    const prefix = lang === 'fr' ? '/fr/propriete' : '/en/property';
    url = `${baseUrl}${prefix}/${slug}`;
  }
  if (url) {
    listing.url = url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? url : '/' + url}`;
  }

  // Date Posted (omit if missing)
  const rawDate = data.datePosted || data.created_at || data.createdAt;
  if (rawDate) {
    try {
      const d =
        typeof (rawDate as any)?.toDate === 'function'
          ? (rawDate as any).toDate()
          : new Date(rawDate as any);
      if (!isNaN(d.getTime())) {
        listing.datePosted = d.toISOString();
      }
    } catch {
      // omit if unparseable
    }
  }

  // Images (omit if missing)
  const rawImages = Array.isArray(data.image)
    ? data.image
    : Array.isArray(data.images)
    ? data.images
    : data.primary_image || data.imageUrl
    ? [data.primary_image || data.imageUrl]
    : [];

  const validImages = rawImages
    .filter(Boolean)
    .map((img: string) => (img.startsWith('http') ? img : `${baseUrl}${img.startsWith('/') ? img : '/' + img}`));

  if (validImages.length > 0) {
    listing.image = validImages;
  }

  // Description (omit if missing)
  const desc =
    (data.localized_description && data.localized_description[lang]) ||
    data.description;
  if (desc && typeof desc === 'string' && desc.trim()) {
    listing.description = desc.replace(/<[^>]*>/g, '').trim().slice(0, 500);
  }

  // Offers (price, priceCurrency "XAF") - omit if price missing or 0
  const priceVal =
    typeof data.price === 'number'
      ? data.price
      : parseFloat(String(data.price || '').replace(/[^0-9.]/g, ''));

  if (!isNaN(priceVal) && priceVal > 0) {
    listing.offers = {
      '@type': 'Offer',
      price: priceVal,
      priceCurrency: 'XAF',
    };
  }

  // Content Location (PostalAddress: locality, region, country "CM")
  const locality = data.locality || data.city || data.location?.split(',')?.[0]?.trim();
  const region = data.region || data.addressRegion;

  const postalAddress: Record<string, string> = {
    '@type': 'PostalAddress',
    addressCountry: 'CM',
  };

  if (locality && locality.trim()) {
    postalAddress.addressLocality = locality.trim();
  }
  if (region && region.trim()) {
    postalAddress.addressRegion = region.trim();
  }

  listing.contentLocation = postalAddress;

  return listing;
}

/**
 * 5. Guides: Article
 * Headline, author, datePublished, dateModified, inLanguage.
 * Rule: Never invent data; omit any property whose data is missing.
 */
export function buildArticleJsonLd(
  data: ArticleGuideData,
  options: { lang?: 'en' | 'fr'; baseUrl?: string } = {}
) {
  if (!data) return null;
  const lang = options.lang || (data.inLanguage?.startsWith('fr') ? 'fr' : 'en');
  const baseUrl = (options.baseUrl || BASE_URL).replace(/\/+$/, '');

  const article: Record<string, any> = {
    '@type': 'Article',
    inLanguage: lang === 'fr' ? 'fr-CM' : 'en-CM',
  };

  // Headline (omit if missing)
  const headline = data.headline || data.title;
  if (headline && typeof headline === 'string' && headline.trim()) {
    article.headline = headline.split('|')[0].trim();
  }

  // Author
  if (data.author) {
    if (typeof data.author === 'string') {
      article.author = {
        '@type': 'Organization',
        name: data.author.trim(),
      };
    } else if (typeof data.author === 'object') {
      article.author = data.author;
    }
  } else {
    article.author = {
      '@type': 'Organization',
      name: 'Cameroon Land Market',
      url: baseUrl,
    };
  }

  // Date Published (omit if missing)
  const rawPub = data.datePublished || data.publishedAt;
  if (rawPub) {
    try {
      const d =
        typeof (rawPub as any)?.toDate === 'function'
          ? (rawPub as any).toDate()
          : new Date(rawPub as any);
      if (!isNaN(d.getTime())) {
        article.datePublished = d.toISOString();
      }
    } catch {
      // omit if unparseable
    }
  }

  // Date Modified (omit if missing)
  const rawMod = data.dateModified || data.updatedAt || rawPub;
  if (rawMod) {
    try {
      const d =
        typeof (rawMod as any)?.toDate === 'function'
          ? (rawMod as any).toDate()
          : new Date(rawMod as any);
      if (!isNaN(d.getTime())) {
        article.dateModified = d.toISOString();
      }
    } catch {
      // omit if unparseable
    }
  }

  // Description (omit if missing)
  if (data.description && typeof data.description === 'string' && data.description.trim()) {
    article.description = data.description.replace(/<[^>]*>/g, '').trim().slice(0, 500);
  }

  // URL / mainEntityOfPage
  const url = data.url || data.canonicalUrl || data.path;
  if (url) {
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? url : '/' + url}`;
    article.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': fullUrl,
    };
  }

  // Publisher
  article.publisher = {
    '@type': 'Organization',
    name: 'Cameroon Land Market',
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}/cameroon-land-market-logo.png`,
    },
  };

  return article;
}
